import { useQuery } from '@tanstack/react-query';

import { createQueryOptions } from '../../query';
import { useOfferApi } from '../../src/offers/api';
import { offerKeys } from '../query-key-factory';
import { CategoryLocationSearch, CategorySearch } from './categories.search.types';
import {
  GetOfferCategoriesRequest,
  ListCategoriesResponse,
  ShowCategoryAndLocationsResponse,
  ShowCategoryAndMerchantsResponse,
} from './categories.types';

const useListOfferCategories = ({ queryOptions }: { queryOptions?: CategorySearch }) => {
  const OfferApi = useOfferApi();

  const { queryString, queryObject } = createQueryOptions<CategorySearch>({
    search: queryOptions,
  });

  const getOfferCategories = async (context: GetOfferCategoriesRequest) => {
    const { queryString } = context.meta || {};
    const { data } = await OfferApi.get<ListCategoriesResponse>(`/categories?${queryString}`);
    return data;
  };

  const key = offerKeys.categories.all;
  return useQuery({
    queryKey: [...key, queryObject],
    queryFn: getOfferCategories,
    meta: { queryString },
  });
};

const useShowCategoryAndLocations = ({
  categoryId,
  queryOptions,
}: {
  categoryId: string;
  queryOptions?: CategoryLocationSearch;
}) => {
  const OfferApi = useOfferApi();

  const { queryString, queryObject } = createQueryOptions<CategoryLocationSearch>({
    search: queryOptions,
  });
  const getCategoryAndLocations = async () => {
    const { data } = await OfferApi.get<ShowCategoryAndLocationsResponse>(
      `/categories/${categoryId}/locations?${queryString}`
    );
    return data;
  };

  const key = offerKeys.categories.id(categoryId).locations.all;

  return useQuery({
    queryKey: [...key, queryObject],
    queryFn: getCategoryAndLocations,
  });
};

const useShowCategoryAndMerchants = ({ categoryId }: { categoryId: string }) => {
  const OfferApi = useOfferApi();

  const getCategoryAndMerchants = async () => {
    const { data } = await OfferApi.get<ShowCategoryAndMerchantsResponse>(
      `/categories/${categoryId}/merchants`
    );
    return data;
  };

  return useQuery({
    queryKey: offerKeys.categories.id(categoryId).merchants.all,
    queryFn: getCategoryAndMerchants,
  });
};

export { useListOfferCategories, useShowCategoryAndLocations, useShowCategoryAndMerchants };
